/**
 * Product catalog for the affiliate programme.
 *
 * This is where a brand lists what creators can promote. A product only
 * becomes visible to creators once it is ACTIVE *and* a commission rule
 * applies to it -- the list surfaces both, because "why can no creator see my
 * product" is otherwise an invisible two-part answer.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AddIcon from '@mui/icons-material/Add';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SectionCard from '../../_helpers/reusableComponents/design/SectionCard';
import EmptyState from '../../_helpers/reusableComponents/design/EmptyState';
import StatusPill from '../../_helpers/reusableComponents/design/StatusPill';
import PrimaryButton, {
  SecondaryButton,
} from '../../_helpers/reusableComponents/design/PrimaryButton';
import {
  palette,
  radius,
  shadow,
  motion,
  typography,
} from '../../_helpers/reusableComponents/design/designTokens';
import CommerceService, { readApiError } from '../../_services/commerce/CommerceService';

const EMPTY_FORM = {
  title: '',
  sku: '',
  slug: '',
  description: '',
  category: '',
  base_price: '',
  compare_at_price: '',
  stock_quantity: 0,
  primary_image_url: '',
  status: 'ACTIVE',
  commission_eligible: true,
};

// Shared field styling, so every input in the dialog has the same corner
// radius and focus colour rather than three near-identical sx blocks.
const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${radius.sm}px`,
    bgcolor: palette.surface,
    '& fieldset': { borderColor: palette.line },
    '&:hover fieldset': { borderColor: palette.hint },
    '&.Mui-focused fieldset': { borderColor: palette.brand, borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: palette.brand },
};

const HEAD_ROW_SX = {
  '& th': {
    ...typography.eyebrow,
    bgcolor: palette.surfaceAlt,
    borderBottom: `1px solid ${palette.line}`,
    py: 1.25,
    whiteSpace: 'nowrap',
  },
};

const BODY_ROW_SX = {
  transition: `background-color ${motion.fast}`,
  '&:hover': { backgroundColor: palette.brandSofter },
  '&:last-of-type td': { borderBottom: 0 },
};

/** Slugify locally so the creator-facing URL is visible before saving. */
const toSlug = (value) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 140);

/**
 * Two separate conditions decide whether a creator can earn on this product.
 * Reporting them apart -- as early returns rather than a chained ternary --
 * means the fix is obvious instead of guesswork.
 *
 * The pill is wrapped in a span because a Tooltip has to hand a ref to its
 * child, and the reason it exists is to say *why* a product is invisible.
 */
const CreatorVisibilityChip = ({ product }) => {
  if (!product.commission_eligible) {
    return (
      <Tooltip arrow title="Marked not commissionable on the product itself">
        <Box component="span" sx={{ display: 'inline-flex' }}>
          <StatusPill status="DRAFT" label="Not commissionable" />
        </Box>
      </Tooltip>
    );
  }

  if (product.has_commission_rule) {
    return <StatusPill status="ACTIVE" label="Earning enabled" />;
  }

  return (
    <Tooltip
      arrow
      title="No commission rule applies to this product yet, so creators would earn nothing"
    >
      <Box component="span" sx={{ display: 'inline-flex' }}>
        <StatusPill status="PENDING" label="No rule yet" />
      </Box>
    </Tooltip>
  );
};

/**
 * A 40px thumbnail, falling back to a tinted placeholder rather than a broken
 * image icon — a catalog row with no picture should still line up with the
 * rows that have one.
 */
const ProductThumb = ({ url, alt }) => (
  <Box
    sx={{
      width: 40,
      height: 40,
      flexShrink: 0,
      borderRadius: `${radius.sm}px`,
      overflow: 'hidden',
      bgcolor: palette.brandSoft,
      color: palette.brand,
      border: `1px solid ${palette.line}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {url ? (
      <Box
        component="img"
        src={url}
        alt={alt}
        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : (
      <ImageOutlinedIcon sx={{ fontSize: 18 }} />
    )}
  </Box>
);

const ProductManagement = ({ onNotify }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const notify = useCallback(
    (message, severity = 'info') => {
      if (onNotify) onNotify(message, severity);
    },
    [onNotify]
  );

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      const { data } = await CommerceService.listProducts(params);
      setProducts(data.results || data);
    } catch (error) {
      notify(readApiError(error, 'Could not load products.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [search, notify]);

  useEffect(() => {
    // Debounced so typing in the search box does not fire a request per key.
    const timer = setTimeout(loadProducts, 300);
    return () => clearTimeout(timer);
  }, [loadProducts]);

  useEffect(() => {
    CommerceService.listCategories()
      .then(({ data }) => setCategories(data.results || data))
      .catch(() => notify('Could not load categories.', 'error'));
  }, [notify]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditing(product);
    setForm({
      title: product.title || '',
      sku: product.sku || '',
      slug: product.slug || '',
      description: product.description || '',
      category: product.category || '',
      base_price: product.base_price || '',
      compare_at_price: product.compare_at_price || '',
      stock_quantity: product.stock_quantity ?? 0,
      primary_image_url: product.primary_image_url || '',
      status: product.status || 'ACTIVE',
      commission_eligible: product.commission_eligible,
    });
    setErrors({});
    setDialogOpen(true);
  };

  const setField = (field) => (event) => {
    const value =
      event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Keep the slug in step with the title until someone edits it by hand.
      if (field === 'title' && !editing && !prev.slug) {
        next.slug = toSlug(value);
      }
      return next;
    });
  };

  const validate = () => {
    const found = {};
    if (!form.title.trim()) found.title = 'Required';
    if (!form.sku.trim()) found.sku = 'Required';
    if (!form.slug.trim()) found.slug = 'Required';
    if (form.base_price === '' || Number(form.base_price) < 0) {
      found.base_price = 'Enter a price of 0 or more';
    }
    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        category: form.category || null,
        // Money goes to the API as a STRING. Sending a JS number would send
        // it through a float, and 1999.50 does not survive that intact.
        base_price: String(form.base_price),
        compare_at_price: form.compare_at_price
          ? String(form.compare_at_price)
          : null,
        stock_quantity: Number(form.stock_quantity) || 0,
      };

      if (editing) {
        await CommerceService.updateProduct(editing.public_id, payload);
        notify('Product updated.', 'success');
      } else {
        await CommerceService.createProduct(payload);
        notify('Product created. It will appear to creators after the next sync.', 'success');
      }
      setDialogOpen(false);
      loadProducts();
    } catch (error) {
      notify(readApiError(error, 'Could not save the product.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (product) => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Archive "${product.title}"? Creators will stop seeing it, but ` +
          'existing storefronts and order history stay intact.'
      )
    ) {
      return;
    }
    try {
      await CommerceService.archiveProduct(product.public_id);
      notify('Product archived.', 'success');
      loadProducts();
    } catch (error) {
      notify(readApiError(error, 'Could not archive the product.'), 'error');
    }
  };

  // Loading, empty and populated are three peer states; chaining them as
  // nested ternaries inside the JSX read as a single condition.
  let productList;
  if (loading) {
    productList = (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: palette.brand }} />
      </Box>
    );
  } else if (products.length === 0) {
    productList = (
      <Box sx={{ p: 2.5 }}>
        <EmptyState
          icon={<Inventory2OutlinedIcon sx={{ fontSize: 30 }} />}
          title="No products yet"
          description="Add a product and set a commission rule, and creators will be able to promote it."
          action={
            <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
              Add your first product
            </PrimaryButton>
          }
        />
      </Box>
    );
  } else {
    productList = (
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 860 }}>
          <TableHead>
            <TableRow sx={HEAD_ROW_SX}>
              <TableCell>Product</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Stock</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Creator visibility</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.public_id} hover sx={BODY_ROW_SX}>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <ProductThumb url={product.primary_image_url} alt={product.title} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{ fontSize: 14, fontWeight: 700, color: palette.ink }}
                      >
                        {product.title}
                      </Typography>
                      <Typography sx={{ ...typography.mono, color: palette.muted }}>
                        {product.public_path}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...typography.mono, color: palette.inkSoft }}>
                  {product.sku}
                </TableCell>
                {/* Money right-aligned and tabular so the column can be read
                    down as figures rather than across as text. */}
                <TableCell
                  align="right"
                  sx={{ ...typography.money, fontWeight: 700, color: palette.ink }}
                >
                  {`${product.currency} ${product.base_price}`}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ ...typography.money, color: palette.inkSoft }}
                >
                  {product.stock_quantity}
                </TableCell>
                <TableCell>
                  <StatusPill status={product.status} />
                </TableCell>
                <TableCell>
                  <CreatorVisibilityChip product={product} />
                </TableCell>
                <TableCell align="right">
                  {/* Edit first, archive second — the order the tests and the
                      muscle memory both depend on. */}
                  <Tooltip arrow title="Edit product">
                    <IconButton
                      size="small"
                      onClick={() => openEdit(product)}
                      sx={{ color: palette.muted, '&:hover': { color: palette.brand, bgcolor: palette.brandSoft } }}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip arrow title="Archive product">
                    <IconButton
                      size="small"
                      onClick={() => handleArchive(product)}
                      sx={{ color: palette.muted, '&:hover': { color: palette.danger, bgcolor: palette.dangerSoft } }}
                    >
                      <ArchiveOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box>
      <SectionCard
        title="Products"
        subtitle="Everything a creator can pick up and promote."
        noPadBody
        actions={
          <>
            <TextField
              size="small"
              placeholder="Search by title or SKU"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: palette.hint }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...FIELD_SX, minWidth: { xs: '100%', sm: 240 } }}
            />
            <PrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
              Add product
            </PrimaryButton>
          </>
        }
      >
        {productList}
      </SectionCard>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: `${radius.lg}px`, boxShadow: shadow.modal, overflow: 'hidden' },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ px: 3, py: 2.25, borderBottom: `1px solid ${palette.line}`, bgcolor: palette.surfaceAlt }}
          >
            <Box
              sx={{
                width: 36, height: 36, flexShrink: 0,
                borderRadius: `${radius.sm}px`,
                bgcolor: palette.brandSoft, color: palette.brand,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography sx={typography.sectionTitle}>
              {editing ? 'Edit product' : 'Add product'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3, bgcolor: palette.surface }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <Typography sx={typography.eyebrow}>The basics</Typography>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={setField('title')}
                error={Boolean(errors.title)}
                helperText={errors.title}
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="SKU"
                value={form.sku}
                onChange={setField('sku')}
                error={Boolean(errors.sku)}
                helperText={errors.sku}
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Slug"
                value={form.slug}
                onChange={setField('slug')}
                error={Boolean(errors.slug)}
                helperText={
                  errors.slug ||
                  `Creators' links will point at /p/${form.slug || '<slug>'}`
                }
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                value={form.description}
                onChange={setField('description')}
                sx={FIELD_SX}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ ...typography.eyebrow, mt: 1 }}>
                Placement &amp; pricing
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Category"
                value={form.category}
                onChange={setField('category')}
                helperText="Commission rules are often set per category"
                sx={FIELD_SX}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={form.status}
                onChange={setField('status')}
                sx={FIELD_SX}
              >
                {['DRAFT', 'ACTIVE', 'PAUSED', 'ARCHIVED'].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Price"
                value={form.base_price}
                onChange={setField('base_price')}
                error={Boolean(errors.base_price)}
                helperText={errors.base_price}
                inputProps={{ inputMode: 'decimal' }}
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Compare-at price"
                value={form.compare_at_price}
                onChange={setField('compare_at_price')}
                inputProps={{ inputMode: 'decimal' }}
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock"
                type="number"
                value={form.stock_quantity}
                onChange={setField('stock_quantity')}
                sx={FIELD_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL"
                value={form.primary_image_url}
                onChange={setField('primary_image_url')}
                sx={FIELD_SX}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: `1px solid ${palette.line}`, bgcolor: palette.surfaceAlt }}
        >
          <SecondaryButton onClick={() => setDialogOpen(false)}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave} loader={saving}>
            Save
          </PrimaryButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductManagement;
